import React, {useState} from 'react';
import {NewChatData, useNewChatStore} from "../../../store";
import Button from "../../../components/ui/Button/Button";
import Telegram from './telegram.png';
import WhatsApp from './whatsapp.png';
import Input from "../../../components/ui/Input/Input";
import {ArrowRight, CircleHelp, FileCheck2, UploadCloud} from "lucide-react";
import StepAside from "../StepAside";
import {send_file_to_parse} from "../../../api/file_api";
import {isAlreadyToasted} from "../../../api/instance";
import {useLoaderStore} from "../../../store/loader/loader.store";
import {toast} from "react-toastify";
import ExportGuide, {
    detectExportDevice,
    ExportDevice,
    isExportGuideSeen,
    telegramExportSteps,
    whatsappExportSteps
} from "../../../components/layout/ExportGuide/ExportGuide";


interface appsInterface {
    name: NewChatData['chat_type'],
    component: string;
}

const apps: appsInterface[] = [
    {name: "Whatsapp", component: WhatsApp},
    {name: "Telegram", component: Telegram},
];

// WhatsApp on iPhone exports a .zip; Android usually shares a plain .txt (sometimes zipped).
// Telegram Desktop exports result.json or HTML; Telegram for macOS only HTML (messages.html, or the folder as a .zip)
const fileAcceptType = {
    'Whatsapp': {
        name: '.zip or .txt',
        type: '.zip,.txt,application/zip,text/plain',
    },
    'Telegram': {
        name: '.json, .html or .zip',
        type: '.json,.html,.htm,.zip,application/json,text/html,application/zip',
    }
}

const Step2 = () => {

    const {currentStep, setStep, newChatData, updateChatData} = useNewChatStore();
    const {showLoader, hideLoader} = useLoaderStore();
    const [device, setDevice] = useState<ExportDevice>(detectExportDevice);
    // The export guide opens by itself the first time someone imports from each messenger
    const [guideOpen, setGuideOpen] = useState(() => !isExportGuideSeen(newChatData.chat_type));

    const selectTheApp = (app: NewChatData['chat_type']) => {
        updateChatData({chat_type: app, chat_file: null, chat_members: null, person_to_copy: ''})
        if (!isExportGuideSeen(app)) setGuideOpen(true);
    };

    const disableButton = () => {
        return !newChatData.chat_file;
    }

    const changeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        // A new file has its own participants, so the ones parsed from the previous file are dropped
        updateChatData({chat_file: e.target.files?.[0] || null, chat_members: null, person_to_copy: ''});
    };

    const sendFile = async () => {
        // Если участники уже есть — просто идём дальше
        if (newChatData.chat_members?.length) {
            return setStep(currentStep + 1);
        }

        // Если их нет — начинаем парсить
        if (!newChatData.chat_file) return;

        try {
            showLoader("Parsing the chat...");

            const response = await send_file_to_parse(newChatData.chat_file, newChatData.chat_type);

            if (response?.data?.people) {
                updateChatData({chat_members: response.data.people});
            }

            setStep(currentStep + 1);

        } catch (error: any) {
            console.error("sendFile error:", error);
            if (!isAlreadyToasted(error)) {
                toast(error?.response?.data?.message || "Failed to parse chat file", {type: "error"});
            }

        } finally {
            hideLoader();
        }
    };

    return (
        <div className="StepLayout">
            <div className="StepLayout__left">
                <span className="eyebrow">Step {currentStep} of 4</span>
                <h2>Import the chat history</h2>
                <p className="StepLayout__description">We read the messages to learn how the person writes: their
                    words, humour and the memories you share.</p>

                <div className="StepLayout__field">
                    <span className="StepLayout__label">Messenger</span>
                    <div className="StepLayout__selector">
                        {
                            apps.map((el) => (
                                <button type="button" key={el.name} onClick={() => selectTheApp(el.name)}
                                        aria-pressed={newChatData.chat_type === el.name}
                                        className={`StepLayout__selector_option ${newChatData.chat_type === el.name ? 'active' : ''}`}>
                                    <span className="icon-tile StepLayout__appIcon">
                                        <img src={el.component} alt=""/>
                                    </span>
                                    <h3>{el.name === 'Whatsapp' ? 'WhatsApp' : el.name}</h3>
                                    <div className='circle'/>
                                </button>
                            ))
                        }
                    </div>
                </div>

                <div className="StepLayout__field">
                    <div className="StepLayout__labelRow">
                        <span className="StepLayout__label">Chat export file</span>
                        <button type="button" className="StepLayout__helpLink" onClick={() => setGuideOpen(true)}>
                            <CircleHelp size={14}/> How to export
                        </button>
                    </div>
                    <label className={`file-selector ${newChatData.chat_file ? 'file-selector--done' : ''}`}>
                        <Input hidden onChange={changeFile}
                               accept={fileAcceptType[newChatData.chat_type].type}
                               file={true}
                               title={newChatData.chat_file ? 'Click to choose another file'
                                   : `Click to upload the exported ${fileAcceptType[newChatData.chat_type].name} file`}
                               type={'file'}/>

                        <span className="icon-tile">
                            {newChatData.chat_file ? <FileCheck2 size={20}/> : <UploadCloud size={20}/>}
                        </span>

                        {
                            newChatData.chat_file &&
                            <span className="file-selector__name">{newChatData.chat_file.name}</span>
                        }
                    </label>
                </div>

                <div className="StepLayout__buttons">
                    <Button type={'button'} onClick={() => setStep(currentStep - 1)} variant={'secondary'}
                            title={'Back'}/>
                    <Button type={'button'} disabled={disableButton()} onClick={() => sendFile()} variant={'primary'}
                            title={'Continue'} icon={<ArrowRight size={16}/>}/>
                </div>

            </div>

            <StepAside title={`How to export from ${newChatData.chat_type === 'Whatsapp' ? 'WhatsApp' : 'Telegram'}`}>
                {
                    newChatData.chat_type === 'Whatsapp' &&
                    <div className="StepLayout__tipTabs" role="group" aria-label="Your phone">
                        {(['iPhone', 'Android'] as ExportDevice[]).map((el) => (
                            <button type="button" key={el} aria-pressed={device === el}
                                    className={device === el ? 'active' : ''} onClick={() => setDevice(el)}>
                                {el}
                            </button>
                        ))}
                    </div>
                }
                <ol className="StepLayout__tipSteps">
                    {(newChatData.chat_type === 'Whatsapp' ? whatsappExportSteps[device] : telegramExportSteps)
                        .map((step) => <li key={step}>{step}</li>)}
                </ol>
            </StepAside>

            {guideOpen && <ExportGuide chatType={newChatData.chat_type} onClose={() => setGuideOpen(false)}/>}
        </div>
    );
};

export default Step2;
